import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import type { updateFavouriteOrderMutation } from '@atlassian/jira-relay/src/__generated__/updateFavouriteOrderMutation.graphql.ts';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { MenuItemReorderOperation } from '../../utils/constants.tsx';
import { useHandleError } from './error-handling.tsx';
import { optimisticUpdater } from './optimistic-updater/index.tsx';

export const useUpdateFavouriteOrder = (favouriteConnectionId: string) => {
	const cloudId = useCloudId();

	const [commitMutation] = useMutation<updateFavouriteOrderMutation>(graphql`
		mutation updateFavouriteOrderMutation($input: JiraReorderSidebarMenuItemInput!) {
			jira_reorderProjectsSidebarMenuItem(input: $input) @optIn(to: "JiraSidebarMenu") {
				id
				favourites(first: 15) {
					__id
					pageInfo {
						hasNextPage
					}
					edges {
						node {
							id
							projectKey: key
							...Nav4ProjectMenuItem
						}
					}
				}
			}
		}
	`);

	const handleError = useHandleError();

	const updateFavouriteOrder = useCallback(
		(operation: MenuItemReorderOperation, projectIds: ProjectId[], projectId: ProjectId) => {
			commitMutation({
				variables: {
					input: {
						cloudId,
						reorderMode: operation,
						menuItem: {
							itemId: projectId,
						},
					},
				},
				optimisticUpdater: (store) => {
					optimisticUpdater(store, favouriteConnectionId, operation, projectIds, projectId);
				},
				onCompleted: (_, errors) => {
					if (errors && errors.length > 0) {
						const error = new MutationError(errors[0]);
						handleError(error);
					}
				},
				onError: (error) => {
					handleError(new MutationError(error));
				},
			});
		},
		[commitMutation, cloudId, favouriteConnectionId, handleError],
	);

	return updateFavouriteOrder;
};
